// ===========================================
// Teachers Management Page
// ===========================================
// CRUD interface for managing teachers

import { useState, useMemo } from 'react';
import {
  Box,
  TextField,
  Alert,
  Chip,
  Stack,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  OutlinedInput,
  Checkbox,
  ListItemText,
  Switch,
  FormControlLabel,
  Typography,
  Button,
  Divider,
  Paper,
  Tooltip,
  Badge,
} from '@mui/material';
import type { SelectChangeEvent } from '@mui/material/Select';
import PageHeader from '../../components/common/PageHeader';
import DataTable, { Column } from '../../components/common/DataTable';
import FormModal from '../../components/common/FormModal';
import ConfirmDialog from '../../components/common/ConfirmDialog';
import ColorPickerField from '../../components/common/ColorPickerField';
import { getContrastTextColor } from '../../styles/brandColors';
import { useInstruments } from '../../hooks/useAdmin';
import { useSchoolInfo } from '../../hooks/useAdminSettings';
import {
  useTeachers,
  useCreateTeacher,
  useUpdateTeacher,
  useDeleteTeacher,
  useAssignInstrument,
  useRemoveInstrument,
  useAddTeacherAvailability,
  useTeacherTaskSummary,
} from '../../hooks/useUsers';
import { Teacher } from '../../api/users.api';
import TeacherAvailabilityEditor from '../../components/admin/TeacherAvailabilityEditor';
import TeacherTimeOffPanel from '../../components/admin/TeacherTimeOffPanel';
import PendingTimeOffDialog from '../../components/admin/PendingTimeOffDialog';
import ViewTeacherDialog from '../../components/admin/ViewTeacherDialog';
import ResetTeacherTasksDialog from '../../components/admin/ResetTeacherTasksDialog';
import { usePendingTimeOff } from '../../hooks/useTeacherUnavailability';
import { usePermissions } from '../../hooks/usePermissions';
import { useOwnerTitle } from '../../hooks/useOwnerTitle';
import AccessChip from '../../components/common/AccessChip';
import OfficeAccessSection from '../../components/admin/OfficeAccessSection';
import { useAuth } from '../../contexts/useAuth';

// ===========================================
// AVAILABILITY CONSTANTS
// ===========================================

const adminIdentityHelper = (ownerTitle: string) =>
  `Only your ${ownerTitle} can change the login email, phone or active status of a login with ${ownerTitle} access or a staff role`;

const DAYS_OF_WEEK = [
  { value: 0, label: 'Sunday' },
  { value: 1, label: 'Monday' },
  { value: 2, label: 'Tuesday' },
  { value: 3, label: 'Wednesday' },
  { value: 4, label: 'Thursday' },
  { value: 5, label: 'Friday' },
  { value: 6, label: 'Saturday' },
];

const DEFAULT_START = '15:00';
const DEFAULT_END = '19:00';

interface DaySlotForm {
  checked: boolean;
  startTime: string;
  endTime: string;
}

const getInitialDaySlots = (): Record<number, DaySlotForm> => {
  const slots: Record<number, DaySlotForm> = {};
  for (const day of DAYS_OF_WEEK) {
    slots[day.value] = { checked: false, startTime: DEFAULT_START, endTime: DEFAULT_END };
  }
  return slots;
};

// ===========================================
// TEACHER TASKS BADGE STYLES
// ===========================================

const taskBadgeSx = {
  width: 28,
  height: 28,
  borderRadius: '50%',
  fontWeight: 600,
  '& .MuiChip-label': { px: 0 },
} as const;

// ===========================================
// COMPONENT
// ===========================================

export default function TeachersPage() {
  const { data: teachers, isLoading, error } = useTeachers();
  const { data: instruments } = useInstruments();
  const { data: schoolInfo } = useSchoolInfo();
  const createMutation = useCreateTeacher();
  const updateMutation = useUpdateTeacher();
  const deleteMutation = useDeleteTeacher();
  const assignInstrumentMutation = useAssignInstrument();
  const removeInstrumentMutation = useRemoveInstrument();
  const addAvailabilityMutation = useAddTeacherAvailability();
  const { data: tasksByTeacher } = useTeacherTaskSummary();

  // Form state
  const [modalOpen, setModalOpen] = useState(false);
  const [editingTeacher, setEditingTeacher] = useState<Teacher | null>(null);
  const [formData, setFormData] = useState({
    email: '',
    firstName: '',
    lastName: '',
    phone: '',
    bio: '',
    instrumentIds: [] as string[],
    color: null as string | null,
    isActive: true,
    workingWithChildrenId: '',
  });

  // View dialog state
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [viewingTeacher, setViewingTeacher] = useState<Teacher | null>(null);

  // Reset Teacher Tasks dialog
  const [resetTasksOpen, setResetTasksOpen] = useState(false);
  const [timeOffInboxOpen, setTimeOffInboxOpen] = useState(false);
  const { can, isOwner } = usePermissions();
  const ownerTitle = useOwnerTitle();
  const ADMIN_IDENTITY_HELPER = adminIdentityHelper(ownerTitle);
  const { user: me } = useAuth();
  // The server refuses a non-owner changing the login email, phone or active state of an ADMIN or a
  // staff login (assertMayChangeIdentity → isProtectedAccount), so the form disables all three
  // rather than failing on Save. `hasStaffRole` comes from the teachers list.
  const identityLocked =
    !!editingTeacher &&
    !isOwner &&
    (editingTeacher.User.role === 'ADMIN' || editingTeacher.hasStaffRole === true);
  // Removing your own Admin is refused by the server (self-demotion guard); Office access disables it.
  const isSelf = !!editingTeacher && editingTeacher.userId === me?.id;
  // Gate the inbox poll on the permission the endpoint enforces, so a staff
  // seat without people_teachers.read doesn't 403 on every page mount.
  const { data: pendingTimeOff } = usePendingTimeOff({
    enabled: can('people_teachers', 'read'),
  });
  const pendingTimeOffCount = pendingTimeOff?.length ?? 0;

  // School-wide outstanding task count (sum of per-teacher totals)
  const totalOutstandingTasks = useMemo(
    () =>
      Object.values(tasksByTeacher ?? {}).reduce(
        (sum, t) => sum + (t?.total ?? 0),
        0
      ),
    [tasksByTeacher]
  );

  // Delete confirmation state
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [teacherToDelete, setTeacherToDelete] = useState<Teacher | null>(null);

  // Filter state
  const [hideInactive, setHideInactive] = useState(false);

  // Availability form state (for add mode)
  const [newTeacherAvailability, setNewTeacherAvailability] = useState<Record<number, DaySlotForm>>(getInitialDaySlots);
  const [availabilityApplyTime, setAvailabilityApplyTime] = useState({ startTime: DEFAULT_START, endTime: DEFAULT_END });

  // Filtered teachers
  const filteredTeachers = useMemo((): Teacher[] => {
    if (!teachers) return [];
    if (hideInactive) {
      return teachers.filter((teacher: Teacher) => teacher.isActive);
    }
    return teachers;
  }, [teachers, hideInactive]);

  // Table columns
  const columns: Column<Teacher>[] = [
    {
      id: 'color',
      label: '',
      minWidth: 40,
      sortable: false,
      format: (value) =>
        value ? (
          <Box
            sx={{
              width: 24,
              height: 24,
              bgcolor: value as string,
              borderRadius: 0.5,
              border: '1px solid',
              borderColor: 'divider',
            }}
          />
        ) : null,
    },
    {
      id: 'user.firstName',
      label: 'Name',
      minWidth: 150,
      format: (_, row) => (
        <Stack direction="row" spacing={1} alignItems="flex-start">
          <Stack>
            <span>{row.User.firstName} {row.User.lastName}</span>
            {schoolInfo?.requireWorkingWithChildrenId && row.workingWithChildrenId && (
              <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5 }}>
                {row.workingWithChildrenId}
              </Typography>
            )}
          </Stack>
          <AccessChip role={row.User.role} staffRole={row.staffRole} />
        </Stack>
      ),
    },
    {
      id: 'User.email',
      label: 'Email',
      minWidth: 200,
      format: (value) => {
        const email = value as string;
        if (!email) return '-';
        return email.length > 30 ? `${email.substring(0, 30)}...` : email;
      },
    },
    {
      id: 'User.phone',
      label: 'Phone',
      minWidth: 160,
      format: (value) => (value as string) || '-',
    },
    {
      id: 'instruments',
      label: 'Instruments',
      minWidth: 200,
      sortable: false,
      format: (_, row) => (
        <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
          {row.TeacherInstrument && row.TeacherInstrument.map((ti) => {
            const instrumentColor = ti.Instrument?.color;
            return ti.Instrument ? (
              <Chip
                key={ti.id}
                label={ti.Instrument.name}
                size="small"
                sx={
                  instrumentColor
                    ? {
                        bgcolor: instrumentColor,
                        color: getContrastTextColor(instrumentColor),
                      }
                    : undefined
                }
                color={!instrumentColor ? 'default' : undefined}
              />
            ) : null;
          })}
        </Stack>
      ),
    },
    {
      id: 'id',
      label: 'Tasks',
      minWidth: 80,
      align: 'center',
      sortable: false,
      format: (_, row) => {
        const tasks = tasksByTeacher?.[row.id];
        if (!tasks || tasks.total === 0) {
          return <Chip label="0" size="small" color="success" sx={taskBadgeSx} />;
        }
        if (tasks.overdue > 0) {
          return (
            <Tooltip title={`${tasks.total} outstanding, ${tasks.overdue} overdue (>72h)`}>
              <Chip
                label={tasks.total}
                size="small"
                sx={{ ...taskBadgeSx, bgcolor: '#FFAE9E', color: '#7A1B0C' }}
              />
            </Tooltip>
          );
        }
        return (
          <Tooltip title={`${tasks.total} outstanding`}>
            <Chip
              label={tasks.total}
              size="small"
              color="warning"
              sx={taskBadgeSx}
            />
          </Tooltip>
        );
      },
    },
    {
      id: 'isActive',
      label: 'Status',
      minWidth: 80,
      format: (value) =>
        value ? (
          <Chip label="Active" size="small" color="success" />
        ) : (
          <Chip label="Inactive" size="small" color="default" />
        ),
    },
  ];

  // Check if teacher can be deleted (must be inactive)
  const canDeleteTeacher = (teacher: Teacher) => !teacher.isActive;

  // Handlers
  const handleAdd = () => {
    setEditingTeacher(null);
    setFormData({
      email: '',
      firstName: '',
      lastName: '',
      phone: '',
      bio: '',
      instrumentIds: [],
      color: null,
      isActive: true,
      workingWithChildrenId: '',
    });
    setNewTeacherAvailability(getInitialDaySlots());
    setAvailabilityApplyTime({ startTime: DEFAULT_START, endTime: DEFAULT_END });
    setModalOpen(true);
  };

  const handleView = (teacher: Teacher) => {
    setViewingTeacher(teacher);
    setViewDialogOpen(true);
  };

  const handleEdit = (teacher: Teacher) => {
    setEditingTeacher(teacher);
    setFormData({
      email: teacher.User.email,
      firstName: teacher.User.firstName,
      lastName: teacher.User.lastName,
      phone: teacher.User.phone || '',
      bio: teacher.bio || '',
      instrumentIds: teacher.TeacherInstrument.map((ti) => ti.Instrument.id),
      color: teacher.color,
      isActive: teacher.isActive,
      workingWithChildrenId: teacher.workingWithChildrenId || '',
    });
    setModalOpen(true);
  };

  const handleDelete = (teacher: Teacher) => {
    setTeacherToDelete(teacher);
    setDeleteDialogOpen(true);
  };

  const handleInstrumentChange = (event: SelectChangeEvent<string[]>) => {
    const value = event.target.value;
    setFormData({
      ...formData,
      instrumentIds: typeof value === 'string' ? value.split(',') : value,
    });
  };

  const handleSubmit = async () => {
    try {
      if (editingTeacher) {
        await updateMutation.mutateAsync({
          id: editingTeacher.id,
          data: {
            ...(formData.email !== editingTeacher.User.email && { email: formData.email }),
            firstName: formData.firstName,
            lastName: formData.lastName,
            phone: formData.phone || undefined,
            bio: formData.bio || undefined,
            color: formData.color,
            isActive: formData.isActive,
            workingWithChildrenId: formData.workingWithChildrenId || undefined,
            // Admin access is the Office access control's own save (roles slice 3), never this one.
          },
        });

        // Sync instrument changes
        const currentIds = editingTeacher.TeacherInstrument.map((ti) => ti.Instrument.id);
        const toAdd = formData.instrumentIds.filter((id) => !currentIds.includes(id));
        const toRemove = currentIds.filter((id) => !formData.instrumentIds.includes(id));

        for (const instrumentId of toAdd) {
          await assignInstrumentMutation.mutateAsync({
            teacherId: editingTeacher.id,
            instrumentId,
          });
        }
        for (const instrumentId of toRemove) {
          await removeInstrumentMutation.mutateAsync({
            teacherId: editingTeacher.id,
            instrumentId,
          });
        }
      } else {
        const created = await createMutation.mutateAsync({
          email: formData.email,
          firstName: formData.firstName,
          lastName: formData.lastName,
          phone: formData.phone || undefined,
          bio: formData.bio || undefined,
          instrumentIds: formData.instrumentIds,
          color: formData.color ?? undefined,
          workingWithChildrenId: formData.workingWithChildrenId || undefined,
        });

        // Create availability slots for the new teacher
        const newTeacherId = created?.id;
        if (newTeacherId) {
          const checkedDays = DAYS_OF_WEEK.filter((d) => newTeacherAvailability[d.value].checked);
          for (const day of checkedDays) {
            const slot = newTeacherAvailability[day.value];
            try {
              await addAvailabilityMutation.mutateAsync({
                teacherId: newTeacherId,
                data: {
                  dayOfWeek: day.value,
                  startTime: slot.startTime,
                  endTime: slot.endTime,
                },
              });
            } catch (error) {
              console.error('Failed to add availability slot:', error);
              // Continue with other slots even if one fails
            }
          }
        }
      }
      setModalOpen(false);
    } catch (error) {
      console.error('Failed to save teacher:', error);
      // Error is handled by mutation
    }
  };

  const handleConfirmDelete = async () => {
    if (teacherToDelete) {
      try {
        await deleteMutation.mutateAsync(teacherToDelete.id);
        setDeleteDialogOpen(false);
        setTeacherToDelete(null);
      } catch {
        // Error is handled by mutation
      }
    }
  };

  const isFormLoading = createMutation.isPending || updateMutation.isPending || assignInstrumentMutation.isPending || removeInstrumentMutation.isPending;
  const isDeleteLoading = deleteMutation.isPending;

  return (
    <Box>
      <PageHeader
        title="Teachers"
        subtitle="Manage teacher accounts and instrument assignments"
        breadcrumbs={[
          { label: 'Admin', path: '/admin' },
          { label: 'Teachers' },
        ]}
        action={
          <Stack direction="row" spacing={1.5}>
            <Badge badgeContent={pendingTimeOffCount} color="secondary">
              <Button variant="outlined" onClick={() => setTimeOffInboxOpen(true)}>
                Time Off
              </Button>
            </Badge>
            <Button variant="outlined" onClick={() => setResetTasksOpen(true)}>
              Reset Tasks
            </Button>
            <Button variant="contained" onClick={handleAdd}>
              Add Teacher
            </Button>
          </Stack>
        }
      />

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          Failed to load teachers. Please try again.
        </Alert>
      )}

      {/* Filters */}
      <Stack
        direction="row"
        spacing={2}
        sx={{ mb: 2 }}
        alignItems="center"
      >
        <FormControlLabel
          control={
            <Switch
              checked={hideInactive}
              onChange={(e) => setHideInactive(e.target.checked)}
              size="small"
            />
          }
          label="Hide inactive teachers"
        />

        {filteredTeachers.length !== (teachers?.length ?? 0) && (
          <Chip
            label={`Showing ${filteredTeachers.length} of ${teachers?.length ?? 0} teachers`}
            size="small"
            variant="outlined"
          />
        )}
      </Stack>

      <DataTable
        columns={columns}
        data={filteredTeachers}
        loading={isLoading}
        onView={handleView}
        onEdit={handleEdit}
        // The bin is a PERMANENT delete (people_teachers.delete, roles 1b §5.15): no bin without it.
        onDelete={can('people_teachers', 'delete') ? handleDelete : undefined}
        canDelete={canDeleteTeacher}
        deleteDisabledTooltip="Set status to Inactive to delete this teacher"
        emptyMessage={
          teachers && teachers.length > 0
            ? 'No teachers match the current filter.'
            : "No teachers found. Click 'Add Teacher' to create one."
        }
        searchPlaceholder="Search teachers..."
      />

      {/* Add/Edit Modal */}
      <FormModal
        open={modalOpen}
        title={editingTeacher ? 'Edit Teacher' : 'Add Teacher'}
        submitLabel={editingTeacher ? 'Update' : 'Create'}
        loading={isFormLoading}
        onSubmit={handleSubmit}
        onClose={() => setModalOpen(false)}
      >
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
          <TextField
            label="Email"
            type="email"
            placeholder="teacher@example.com"
            value={formData.email}
            onChange={(e) => setFormData({ ...formData, email: e.target.value })}
            required
            fullWidth
            disabled={identityLocked}
            helperText={identityLocked ? ADMIN_IDENTITY_HELPER : !editingTeacher ? "We'll email a link to set their password. It works for 7 days." : 'Changing this will update the teacher\'s login email'}
          />
          <Stack direction="row" spacing={2}>
            <TextField
              label="First Name"
              value={formData.firstName}
              onChange={(e) => setFormData({ ...formData, firstName: e.target.value })}
              required
              fullWidth
            />
            <TextField
              label="Last Name"
              value={formData.lastName}
              onChange={(e) => setFormData({ ...formData, lastName: e.target.value })}
              required
              fullWidth
            />
          </Stack>
          <TextField
            label="Phone"
            placeholder="(02) 1234 5678"
            value={formData.phone}
            onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
            fullWidth
            disabled={identityLocked}
            helperText={identityLocked ? ADMIN_IDENTITY_HELPER : undefined}
          />
          {schoolInfo?.requireWorkingWithChildrenId && (
            <TextField
              label="Working with Children ID"
              placeholder="e.g., WWC1234567E"
              value={formData.workingWithChildrenId}
              onChange={(e) => setFormData({ ...formData, workingWithChildrenId: e.target.value })}
              fullWidth
              helperText="Working with Children check registration number (required by your school)"
              required={!editingTeacher}
            />
          )}
          <FormControl fullWidth>
            <InputLabel>Instruments</InputLabel>
            <Select
              multiple
              value={formData.instrumentIds}
              onChange={handleInstrumentChange}
              input={<OutlinedInput label="Instruments" />}
              renderValue={(selected) =>
                instruments
                  ?.filter((i: { id: string; name: string }) => selected.includes(i.id))
                  .map((i: { id: string; name: string }) => i.name)
                  .join(', ')
              }
            >
              {instruments?.map((instrument: { id: string; name: string }) => (
                <MenuItem key={instrument.id} value={instrument.id}>
                  <Checkbox checked={formData.instrumentIds.includes(instrument.id)} />
                  <ListItemText primary={instrument.name} />
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <TextField
            label="Bio"
            placeholder="Brief description of the teacher..."
            value={formData.bio}
            onChange={(e) => setFormData({ ...formData, bio: e.target.value })}
            fullWidth
            multiline
            rows={3}
          />
          <ColorPickerField
            label="Color"
            value={formData.color}
            onChange={(color) => setFormData({ ...formData, color })}
            helperText="Optional color for visual identification on calendars and schedules"
            useSchoolPalette
          />
          {editingTeacher && (
            <Box sx={{ mt: 2 }}>
              <TeacherAvailabilityEditor
                teacherId={editingTeacher.id}
                teacherName={`${editingTeacher.User.firstName} ${editingTeacher.User.lastName}`}
              />
            </Box>
          )}
          {editingTeacher && (
            <Box sx={{ mt: 2 }}>
              <Divider sx={{ mb: 2 }} />
              <TeacherTimeOffPanel
                teacherId={editingTeacher.id}
                teacherName={`${editingTeacher.User.firstName} ${editingTeacher.User.lastName}`}
              />
            </Box>
          )}
          {editingTeacher && (
            <Box sx={{ mt: 1 }}>
              <FormControlLabel
                control={
                  <Switch
                    disabled={identityLocked}
                    checked={formData.isActive}
                    onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })}
                  />
                }
                label={formData.isActive ? 'Active' : 'Inactive'}
              />
              <Typography variant="caption" color="text.secondary" display="block" sx={{ ml: 4.5 }}>
                {identityLocked
                  ? ADMIN_IDENTITY_HELPER
                  : 'Inactive teachers cannot log in. A teacher must be set to inactive before they can be deleted.'}
              </Typography>
            </Box>
          )}
          {editingTeacher && isOwner && (
            // Keyed on the teacher: each Edit starts from that teacher's stored access.
            <OfficeAccessSection key={editingTeacher.id} teacher={editingTeacher} isSelf={isSelf} />
          )}
          {!editingTeacher && (
            <Box sx={{ mt: 2 }}>
              <Divider sx={{ mb: 2 }} />
              <Typography variant="h6" gutterBottom>
                Teaching Availability
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Set when this teacher is available to teach. Leave unchecked to allow scheduling at any time.
              </Typography>

              {/* Apply to all controls */}
              <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1.5 }}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={DAYS_OF_WEEK.every((d) => newTeacherAvailability[d.value].checked)}
                        indeterminate={
                          DAYS_OF_WEEK.some((d) => newTeacherAvailability[d.value].checked) &&
                          !DAYS_OF_WEEK.every((d) => newTeacherAvailability[d.value].checked)
                        }
                        onChange={() => {
                          const allChecked = DAYS_OF_WEEK.every((d) => newTeacherAvailability[d.value].checked);
                          setNewTeacherAvailability((prev) => {
                            const updated = { ...prev };
                            for (const day of DAYS_OF_WEEK) {
                              updated[day.value] = { ...updated[day.value], checked: !allChecked };
                            }
                            return updated;
                          });
                        }}
                        size="small"
                      />
                    }
                    label={<Typography variant="subtitle2">Select All Days</Typography>}
                    sx={{ mr: 'auto' }}
                  />
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                  <TextField
                    label="Start"
                    type="time"
                    size="small"
                    value={availabilityApplyTime.startTime}
                    onChange={(e) => setAvailabilityApplyTime((prev) => ({ ...prev, startTime: e.target.value }))}
                    InputLabelProps={{ shrink: true }}
                    inputProps={{ step: 300 }}
                    sx={{ flex: 1 }}
                  />
                  <TextField
                    label="End"
                    type="time"
                    size="small"
                    value={availabilityApplyTime.endTime}
                    onChange={(e) => setAvailabilityApplyTime((prev) => ({ ...prev, endTime: e.target.value }))}
                    InputLabelProps={{ shrink: true }}
                    inputProps={{ step: 300 }}
                    sx={{ flex: 1 }}
                  />
                  <Button
                    variant="outlined"
                    size="small"
                    onClick={() => {
                      setNewTeacherAvailability((prev) => {
                        const updated = { ...prev };
                        for (const day of DAYS_OF_WEEK) {
                          updated[day.value] = {
                            ...updated[day.value],
                            startTime: availabilityApplyTime.startTime,
                            endTime: availabilityApplyTime.endTime,
                          };
                        }
                        return updated;
                      });
                    }}
                    sx={{ whiteSpace: 'nowrap' }}
                  >
                    Apply to All
                  </Button>
                </Box>
              </Paper>

              {/* Per-day rows */}
              {DAYS_OF_WEEK.map((day) => {
                const slot = newTeacherAvailability[day.value];
                const timeInvalid = slot.checked && slot.startTime >= slot.endTime;
                return (
                  <Box
                    key={day.value}
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1.5,
                      mb: 1,
                      opacity: slot.checked ? 1 : 0.5,
                    }}
                  >
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={slot.checked}
                          onChange={() =>
                            setNewTeacherAvailability((prev) => ({
                              ...prev,
                              [day.value]: { ...prev[day.value], checked: !prev[day.value].checked },
                            }))
                          }
                          size="small"
                        />
                      }
                      label={<Typography variant="body2" sx={{ width: 80 }}>{day.label}</Typography>}
                      sx={{ mr: 0, minWidth: 140 }}
                    />
                    <TextField
                      type="time"
                      size="small"
                      value={slot.startTime}
                      onChange={(e) =>
                        setNewTeacherAvailability((prev) => ({
                          ...prev,
                          [day.value]: { ...prev[day.value], startTime: e.target.value },
                        }))
                      }
                      disabled={!slot.checked}
                      error={timeInvalid}
                      InputLabelProps={{ shrink: true }}
                      inputProps={{ step: 300 }}
                      sx={{ flex: 1 }}
                    />
                    <Typography variant="body2" color="text.secondary">to</Typography>
                    <TextField
                      type="time"
                      size="small"
                      value={slot.endTime}
                      onChange={(e) =>
                        setNewTeacherAvailability((prev) => ({
                          ...prev,
                          [day.value]: { ...prev[day.value], endTime: e.target.value },
                        }))
                      }
                      disabled={!slot.checked}
                      error={timeInvalid}
                      InputLabelProps={{ shrink: true }}
                      inputProps={{ step: 300 }}
                      sx={{ flex: 1 }}
                    />
                  </Box>
                );
              })}
              {DAYS_OF_WEEK.some(
                (d) => newTeacherAvailability[d.value].checked && newTeacherAvailability[d.value].startTime >= newTeacherAvailability[d.value].endTime
              ) && (
                <Alert severity="warning" sx={{ mt: 1 }}>
                  Start time must be before end time for all selected days.
                </Alert>
              )}
            </Box>
          )}
        </Box>
      </FormModal>

      {/* Delete Confirmation */}
      <ConfirmDialog
        open={deleteDialogOpen}
        title="Delete Teacher"
        message={`Are you sure you want to permanently delete ${teacherToDelete?.User.firstName} ${teacherToDelete?.User.lastName}? This action cannot be undone.`}
        confirmLabel="Delete"
        confirmColor="error"
        loading={isDeleteLoading}
        onConfirm={handleConfirmDelete}
        onCancel={() => setDeleteDialogOpen(false)}
      />

      {/* View Teacher Dialog */}
      <ViewTeacherDialog
        open={viewDialogOpen}
        teacher={viewingTeacher}
        // #589 D3: a plain teacher login needs people_teachers.write; an ADMIN or staff-role
        // login is the owner's to send to (the server refuses anyone else).
        allowSendLoginLink={
          can('people_teachers', 'write') &&
          (isOwner ||
            (!!viewingTeacher &&
              !(viewingTeacher.User.role === 'ADMIN' || viewingTeacher.hasStaffRole === true)))
        }
        onClose={() => setViewDialogOpen(false)}
        onEdit={() => {
          setViewDialogOpen(false);
          if (viewingTeacher) handleEdit(viewingTeacher);
        }}
      />

      <ResetTeacherTasksDialog
        open={resetTasksOpen}
        onClose={() => setResetTasksOpen(false)}
        outstandingCount={totalOutstandingTasks}
      />

      <PendingTimeOffDialog
        open={timeOffInboxOpen}
        onClose={() => setTimeOffInboxOpen(false)}
      />
    </Box>
  );
}
